import { useReportClock } from '../../reportClock';
import TimelineCard from '../../MainPage/TimelineCard';
import LiveVehicleAlerts from '../../MainPage/LatestActivityCard';
import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, DollarSign, FileText, Mail } from 'react-feather';
import IncomeCard from '../../MainPage/IncomeCard';
import ProfileGreeting from './ProfileGreetingCard';
import {
    formatDashboardCurrency,
    formatDashboardKpiNumber,
    formatDailyIssuedTicketTrend,
    formatReportingFreshnessLabel,
    loadDashboardKpiData,
} from '../../MainPage/activityData';
import { Container, Row, Col } from 'reactstrap';
import i18n from '../../../../i18n';

const emptyIssuedTicketKpi = {
    todayTotal: 0,
    yesterdayTotal: 0,
    percentageChange: 0,
};

const emptyDashboardKpis = {
    loading: true,
    issuedTickets: emptyIssuedTicketKpi,
    vehicleAlerts: {
        threshold: 5,
        totalVehicles: 0,
        totalEntries: 0,
        maxNoticeCount: 0,
        vehiclesOver10: 0,
        vehiclesOver25: 0,
        vehiclesOver50: 0,
    },
    contestations: {
        todayTotal: 0,
        yesterdayTotal: 0,
        percentageChange: 0,
        matchedNoticeCount: 0,
    },
    paymentConfirmations: {
        todayTotal: 0,
        yesterdayTotal: 0,
        percentageChange: 0,
        todayAmount: 0,
        matchedNoticeCount: 0,
        missingAmountCount: 0,
    },
    freshness: {
        latestReportingAt: '',
        ageMinutes: null,
        status: 'unknown',
    },
};

const Dashboard = () => {
    const reportNow = useReportClock();
    const [error, setError] = useState('');
    const [dashboardKpis, setDashboardKpis] = useState(emptyDashboardKpis);

    useEffect(() => {
        let isMounted = true;

        const loadDashboardKpis = async () => {
            try {
                const kpiData = await loadDashboardKpiData(new Date(), { skipCache: true });
                if (isMounted) setError('');

                if (isMounted) {
                    setDashboardKpis({
                        ...emptyDashboardKpis,
                        ...kpiData,
                        loading: false,
                    });
                }
            } catch (error) {
                if (isMounted) setError('Les indicateurs sont indisponibles. Réessayez dans un instant.');

                if (isMounted) {
                    setDashboardKpis({
                        ...emptyDashboardKpis,
                        loading: false,
                    });
                }
            }
        };

        loadDashboardKpis();

        return () => {
            isMounted = false;
        };
    }, [reportNow]);

    const issuedTicketKpi = dashboardKpis.issuedTickets || emptyIssuedTicketKpi;
    const vehicleAlertsKpi = dashboardKpis.vehicleAlerts || emptyDashboardKpis.vehicleAlerts;
    const contestationKpi = dashboardKpis.contestations || emptyDashboardKpis.contestations;
    const paymentConfirmationKpi = dashboardKpis.paymentConfirmations || emptyDashboardKpis.paymentConfirmations;
    const freshnessKpi = dashboardKpis.freshness || emptyDashboardKpis.freshness;
    const kpisLoading = dashboardKpis.loading;

    const issuedTicketTrendSummary = kpisLoading
        ? { label: '...', trend: 'neutral' }
        : formatDailyIssuedTicketTrend(issuedTicketKpi.todayTotal, issuedTicketKpi.yesterdayTotal);
    const issuedTicketAmount = kpisLoading ? '...' : formatDashboardKpiNumber(issuedTicketKpi.todayTotal);
    const issuedTicketYesterdayAmount = kpisLoading ? '...' : formatDashboardKpiNumber(issuedTicketKpi.yesterdayTotal);

    const vehicleAlertsAmount = kpisLoading ? '...' : formatDashboardKpiNumber(vehicleAlertsKpi.totalVehicles);
    const vehicleAlertsPercent = kpisLoading
        ? '...'
        : `${formatDashboardKpiNumber(vehicleAlertsKpi.totalEntries)} avis cumules`;
    const vehicleAlertsTrend = !kpisLoading && vehicleAlertsKpi.totalVehicles > 0 ? 'negative' : 'positive';

    const contestationTrendSummary = kpisLoading
        ? { label: '...', trend: 'neutral' }
        : formatDailyIssuedTicketTrend(contestationKpi.todayTotal, contestationKpi.yesterdayTotal);
    const contestationAmount = kpisLoading ? '...' : formatDashboardKpiNumber(contestationKpi.todayTotal);

    const paymentTrendSummary = kpisLoading
        ? { label: '...', trend: 'neutral' }
        : formatDailyIssuedTicketTrend(paymentConfirmationKpi.todayTotal, paymentConfirmationKpi.yesterdayTotal);
    const paymentAmount = kpisLoading ? '...' : formatDashboardKpiNumber(paymentConfirmationKpi.todayTotal);
    const paymentPercent = kpisLoading
        ? '...'
        : `${formatDashboardCurrency(paymentConfirmationKpi.todayAmount)} confirmes`;

    const freshnessAmount = kpisLoading
        ? '...'
        : freshnessKpi.status === 'fresh'
            ? 'OK'
            : freshnessKpi.status === 'lagging'
                ? 'Retard'
                : freshnessKpi.status === 'stale'
                    ? 'Retard'
                    : '--';
    const freshnessTrend = kpisLoading
        ? 'neutral'
        : freshnessKpi.status === 'fresh'
            ? 'positive'
            : freshnessKpi.status === 'unknown'
                ? 'neutral'
                : 'negative';
    const freshnessPercent = kpisLoading ? '...' : formatReportingFreshnessLabel(freshnessKpi);

    return (
        <Container fluid={true} className="dashboard-default-sec">
            {error && <div role="alert" className="alert alert-danger">{error}</div>}
            <p>Actualisation automatique chaque minute · America/Toronto</p>
            <Row>
                <Col xl="5" className="box-col-12 des-xl-100">
                    <Row>
                        <Col xl="12" md="6" className="box-col-6 des-xl-50">
                            <ProfileGreeting />
                        </Col>
                        <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                            <IncomeCard
                                iconClass={<FileText />}
                                amount={error ? 'Indisponible' : issuedTicketAmount}
                                title={i18n.t('Dashboard_Card_IssuedTickets')}
                                primaryLabel={i18n.t('Dashboard_Card_Today')}
                                secondaryLabel={i18n.t('Dashboard_Card_Yesterday')}
                                secondaryAmount={error ? 'Indisponible' : issuedTicketYesterdayAmount}
                                percent={error ? '' : issuedTicketTrendSummary.label}
                                trend={issuedTicketTrendSummary.trend}
                            />
                        </Col>
                        <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                            <IncomeCard
                                iconClass={<AlertTriangle />}
                                amount={error ? 'Indisponible' : vehicleAlertsAmount}
                                title={i18n.t('Dashboard_Card_VehicleWatch')}
                                percent={error ? '' : vehicleAlertsPercent}
                                trend={vehicleAlertsTrend}
                                href={`${process.env.PUBLIC_URL}/dashboard/alertes-vehicules`}
                            />
                        </Col>
                        <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                            <IncomeCard
                                iconClass={<Mail />}
                                amount={error ? 'Indisponible' : contestationAmount}
                                title={i18n.t('Dashboard_Card_Contestations')}
                                percent={error ? '' : contestationTrendSummary.label}
                                trend={contestationTrendSummary.trend}
                            />
                        </Col>
                        <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                            <IncomeCard
                                iconClass={<DollarSign />}
                                amount={error ? 'Indisponible' : paymentAmount}
                                title={i18n.t('Dashboard_Card_PaymentConfirmations')}
                                percent={error ? '' : paymentPercent}
                                trend={paymentTrendSummary.trend}
                            />
                        </Col>
                        <Col xl="6" md="3" sm="6" className="box-col-3 des-xl-25 rate-sec">
                            <IncomeCard
                                iconClass={<Clock />}
                                amount={error ? 'Indisponible' : freshnessAmount}
                                title={i18n.t('Dashboard_Card_ReportingFreshness')}
                                percent={error ? '' : freshnessPercent}
                                trend={freshnessTrend}
                            />
                        </Col>
                    </Row>
                </Col >
                <Col xl="7"><TimelineCard /><LiveVehicleAlerts /></Col>
            </Row>
        </Container>
    );
};

export default Dashboard;
